import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Package, Trash2, Edit, Save, X, History, SlidersHorizontal, Upload, Image as ImageIcon, Settings2, Sparkles, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { useState, useRef, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import {
  isManagerOrAbove,
  isSuperAdmin as isSuperAdminRole,
  canViewCosts as canViewCostsRole,
} from '@/lib/roles';
import { r2Upload, r2Delete, r2Url } from '@/lib/r2';
import { apiPost } from '@/lib/apiFetch';
import { GROQ_MODEL, jsonSchemaFormat, specsSchema } from '@/lib/groq';
import { formatCurrency, formatDate, formatDateTime, cn } from '@/lib/utils';
import { toast } from 'sonner';
import { writeAuditLog } from '@/lib/audit';
import { useListNavigation } from '@/lib/useListNavigation';
import { HelpTooltip } from '@/components/HelpTooltip';
import { fifoReallocate } from '@/lib/fifoReallocate';
import { useCurrentUser } from '@/lib/useCurrentUser';

const EMPTY_FORM = {
  part_number: '',
  description: '',
  category: '',
  supplier: '',
  current_price: 0,
  status: 'Active',
  total_qty: 0,
  reserved_qty: 0,
  expected_qty: 0,
  on_order_qty: 0,
};

export function ComponentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPrev, hasNext, goPrev, goNext, position: listPosition } = useListNavigation('components', '/components');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteUsage, setDeleteUsage] = useState<{ builds: number; reservations: number; orders: number } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [isEditingSku, setIsEditingSku] = useState(false);
  const [skuForm, setSkuForm] = useState('');
  const [newForm, setNewForm] = useState<any>(EMPTY_FORM);

  // Part number uniqueness check, so a duplicate shows while typing rather
  // than as a failed insert after the whole form is filled in.
  const [existingId, setExistingId] = useState<string | null>(null);
  useEffect(() => {
    if (!isNew) return;
    const partNumber = String(newForm.part_number ?? '').trim();
    if (!partNumber) { setExistingId(null); return; }
    const timer = setTimeout(async () => {
      // Case-insensitive: CPU-AMD-X and cpu-amd-x are the same part to a human.
      // Escape LIKE wildcards so `_` and `%` in a part number match literally.
      const pattern = partNumber.replace(/[\\%_]/g, c => `\\${c}`);
      const { data } = await supabase.from('components').select('id').ilike('part_number', pattern).limit(1);
      setExistingId(data?.[0]?.id ?? null);
    }, 400);
    return () => clearTimeout(timer);
  }, [isNew, newForm.part_number]);

  // Quantity adjustment state
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ total_qty: 0, on_order_qty: 0, notes: '' });

  // Specs editing state
  const [isEditingSpecs, setIsEditingSpecs] = useState(false);
  const [specsForm, setSpecsForm] = useState<Record<string, any>>({});
  const [isAutoFilling, setIsAutoFilling] = useState(false);

  const { data: currentUser } = useCurrentUser();
  // Named for what it actually admits. Four other pages call the narrower
  // ['super_admin','admin'] check `isAdmin`; this one has always included
  // managers, and the shared name hid that.
  const canManage = isManagerOrAbove(currentUser?.role);
  const canViewCosts = canViewCostsRole(currentUser?.role);
  const isSuperAdmin = isSuperAdminRole(currentUser?.role);

  // Component details — query components table directly
  const { data: component, isLoading } = useQuery({
    queryKey: ['component', id],
    queryFn: async () => {
      // Try by UUID first, then by part_number
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id!);
      const { data, error } = await supabase
        .from('components')
        .select('id, part_number, description, category, supplier, total_qty, available_qty, reserved_qty, expected_qty, on_order_qty, current_price, status, build_sku, specs, created_at, updated_at')
        .eq(isUuid ? 'id' : 'part_number', id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id && !isNew,
  });

  const componentId = component?.id;

  // One entry parsed out of the legacy comma-separated build_sku column.
  interface LegacyBuildEntry { sku: string; isEol: boolean }

  // Builds that use this component. build_components is the live join table
  // that every BOM edit writes to; components.build_sku is a legacy column
  // nothing has maintained since an early import, so it is a fallback for rows
  // that predate the table rather than the source of truth. Reading it alone
  // hid every build created since.
  const { data: usedInBuilds } = useQuery({
    queryKey: ['component-builds', component?.part_number, component?.build_sku],
    queryFn: async () => {
      // Parse legacy entries; strip -EOL suffix for lookup but track it
      const entries = (component?.build_sku ?? '')
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean)
        .map((s: string) => ({ sku: s.replace(/-EOL$/i, ''), isEol: /-EOL$/i.test(s) }));

      const { data: bomRows, error: bomErr } = await supabase
        .from('build_components')
        .select('build_id')
        .eq('component_sku', component!.part_number);
      if (bomErr) throw bomErr;

      const buildIds = [...new Set((bomRows ?? []).map((r: any) => r.build_id).filter(Boolean))];
      const legacySkus = [...new Set(entries.map((e: LegacyBuildEntry) => e.sku))];
      if (!buildIds.length && !legacySkus.length) return [];

      // One row can be reachable by either key, so fetch both and de-duplicate.
      const [byId, bySku] = await Promise.all([
        buildIds.length
          ? supabase.from('builds').select('id, sku, description, status').in('id', buildIds)
          : Promise.resolve({ data: [], error: null }),
        legacySkus.length
          ? supabase.from('builds').select('id, sku, description, status').in('sku', legacySkus)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (byId.error) throw byId.error;
      if (bySku.error) throw bySku.error;

      const builds = new Map<string, any>();
      for (const build of [...(byId.data ?? []), ...(bySku.data ?? [])]) {
        if (!builds.has(build.id)) builds.set(build.id, build);
      }

      return [...builds.values()].map((build: any) => ({
        ...build,
        // The legacy column marks a retired pairing per entry; a build known
        // only to the join table carries its own status instead.
        isEol: entries.some((e: LegacyBuildEntry) => e.sku === build.sku && e.isEol),
      }));
    },
    enabled: !!component?.part_number,
  });

  // Recent audit log entries for this component
  const { data: auditEntries, refetch: refetchAudit } = useQuery({
    queryKey: ['component-audit', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_log')
        .select('id, created_at, user_email, action, old_value, new_value, notes')
        .eq('entity_type', 'component')
        .eq('entity_id', componentId!)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) return [];
      return data ?? [];
    },
    enabled: !!componentId && !isNew,
  });

  // Component types (specs schemas)
  const { data: componentTypes } = useQuery({
    queryKey: ['component-types'],
    queryFn: async () => {
      const { data } = await supabase
        .from('component_types')
        .select('id, code, name, sort_order, specs_schema')
        .order('sort_order');
      return data ?? [];
    },
  });

  // Map category text → component_type code
  const CATEGORY_CODE_MAP: Record<string, string> = {
    cpu: 'CPU',
    fan: 'FAN', cooler: 'FAN', coo: 'COO',
    mb: 'MB', mobo: 'MB', motherboard: 'MB',
    ram: 'RAM', memory: 'RAM',
    ssd: 'STORAGE', hdd: 'STORAGE', rom: 'STORAGE', storage: 'STORAGE', nvme: 'STORAGE',
    gpu: 'GPU', vga: 'GPU', graphics: 'GPU',
    psu: 'PSU', power: 'PSU',
    cas: 'CASE', case: 'CASE', chassis: 'CASE', tower: 'CASE', enclosure: 'CASE',
    os: 'OS', windows: 'OS',
    acc: 'ACC',
  };

  const getTypeCode = (category: string | null | undefined): string | null => {
    if (!category) return null;
    const lower = category.toLowerCase();
    for (const [prefix, code] of Object.entries(CATEGORY_CODE_MAP)) {
      if (lower.startsWith(prefix)) return code;
    }
    return null;
  };

  const matchedType = useMemo(() => {
    const code = getTypeCode(component?.category);
    if (!code || !componentTypes) return null;
    return componentTypes.find((t: any) => t.code === code) ?? null;
  }, [component?.category, componentTypes]);

  const specsFields: any[] = matchedType?.specs_schema?.fields ?? [];

  // Distinct categories for edit dropdown. Read from the view, not the table:
  // scanning components dropped everything past PostgREST's 1000-row cap, which
  // ordered by category meant losing ROM ("Storage") off the end.
  const { data: categories } = useQuery({
    queryKey: ['component-categories'],
    queryFn: async () => {
      const { data } = await supabase
        .from('component_categories')
        .select('category')
        .order('category');
      return (data || []).map((r: any) => r.category).filter(Boolean) as string[];
    },
  });

  // Suppliers — hardcoded seed merged with any already in DB
  const KNOWN_SUPPLIERS = ['Action', 'BAB', 'ELKO', 'Imcopex', 'Proks', 'S&K', 'Sharkoon', 'TechStores', 'XTG'];
  const { data: suppliers } = useQuery({
    queryKey: ['component-suppliers'],
    queryFn: async () => {
      const { data } = await supabase
        .from('component_suppliers')
        .select('supplier')
        .order('supplier');
      const fromDb = (data || []).map((r: any) => r.supplier).filter(Boolean) as string[];
      const seen = new Set(KNOWN_SUPPLIERS.map(s => s.toLowerCase()));
      const extras = fromDb.filter(s => !seen.has(s.toLowerCase()));
      return [...KNOWN_SUPPLIERS, ...extras].sort((a, b) => a.localeCompare(b));
    },
  });

  // Component images
  const isCase = ['case', 'cas'].includes(component?.category?.toLowerCase() ?? '');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imgUploading, setImgUploading] = useState(false);
  const [deleteImgId, setDeleteImgId] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const { data: componentImages = [], refetch: refetchImages } = useQuery({
    queryKey: ['component-media', componentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('component_media')
        .select('id, file_path, file_name, caption, created_at')
        .eq('component_id', componentId!)
        .order('created_at', { ascending: true });
      if (error) {
        console.warn('component_media query failed:', error.message);
        return [];
      }
      return (data || []).map((m: any) => ({
        ...m,
        url: r2Url(`build-media/${m.file_path}`),
      }));
    },
    enabled: !!componentId && !isNew,
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || !componentId || isNew) return;
    setImgUploading(true);
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split('.').pop() || 'jpg';
        const filePath = `components/${componentId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
        await r2Upload(`build-media/${filePath}`, file, file.type);
        const { error: dbErr } = await supabase.from('component_media').insert({
          component_id: componentId,
          file_path: filePath,
          file_name: file.name,
        });
        if (dbErr) throw dbErr;
      }
      refetchImages();
      toast.success(`${files.length} image${files.length > 1 ? 's' : ''} uploaded`);
    } catch (err: any) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setImgUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteImageMutation = useMutation({
    mutationFn: async (imageId: string) => {
      const img = componentImages.find((m: any) => m.id === imageId);
      if (img?.file_path) await r2Delete(`build-media/${img.file_path}`);
      const { error } = await supabase.from('component_media').delete().eq('id', imageId);
      if (error) throw error;
    },
    onSuccess: () => {
      refetchImages();
      setDeleteImgId(null);
      toast.success('Image deleted');
    },
    onError: (e: any) => toast.error(`Delete failed: ${e.message}`),
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: any) => {
      const { error } = await supabase
        .from('components')
        .update(updates)
        .eq('id', componentId!);
      if (error) throw error;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['component', id] });
      queryClient.invalidateQueries({ queryKey: ['components'] });
      setIsEditing(false);
      toast.success('Component updated');
      await writeAuditLog({
        action: 'component_updated',
        entity_type: 'component',
        entity_id: componentId,
        entity_label: component?.part_number,
        old_value: {
          description: component?.description,
          category: component?.category,
          supplier: component?.supplier,
          current_price: component?.current_price,
          status: component?.status,
        },
        new_value: editForm,
      });
      refetchAudit();
    },
    onError: (error: any) => toast.error(error.message || 'Failed to update component'),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('components').delete().eq('id', componentId!);
      if (error) throw error;
    },
    onSuccess: async () => {
      await writeAuditLog({
        action: 'component_deleted',
        entity_type: 'component',
        entity_id: componentId,
        entity_label: component?.part_number,
        old_value: { part_number: component?.part_number, description: component?.description },
      });
      toast.success('Component deleted');
      navigate(-1);
    },
    onError: (error: any) => toast.error(error.message || 'Failed to delete component'),
  });

  // SKU rename mutation (super_admin only)
  const skuRenameMutation = useMutation({
    mutationFn: async (newPartNumber: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase.rpc('rename_component_sku', {
        p_component_id: componentId!,
        p_new_part_number: newPartNumber.trim(),
        p_renamed_by: user?.id ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['component', id] });
      queryClient.invalidateQueries({ queryKey: ['components'] });
      setIsEditingSku(false);
      // Reservation lines now carry the new SKU; reallocate so they pick up
      // its stock rather than staying expected until the next stock change.
      if (data?.new_part_number) await fifoReallocate([data.new_part_number]);
      const counts = data?.rows_updated || {};
      toast.success(`SKU renamed. Updated: ${counts.build_components || 0} builds, ${counts.reservation_components || 0} reservations, ${counts.assembly_order_components || 0} orders`);
      await writeAuditLog({
        action: 'sku_renamed',
        entity_type: 'component',
        entity_id: componentId,
        entity_label: data?.new_part_number,
        old_value: { part_number: data?.old_part_number },
        new_value: { part_number: data?.new_part_number, rows_updated: data?.rows_updated },
      });
      refetchAudit();
    },
    onError: (error: any) => toast.error(error.message || 'Failed to rename SKU'),
  });

  // Groq spec auto-fill
  const autoFillSpecs = async (componentId: string, partNumber: string, description: string, category: string, showUI = false) => {
    const typeCode = getTypeCode(category);
    if (!typeCode) { if (showUI) toast.error(`No specs schema for category "${category}"`); else console.warn(`[AutoFill] Skipped: no type code for category "${category}"`); return; }
    const typeDef = componentTypes?.find((t: any) => t.code === typeCode);
    const fields: any[] = typeDef?.specs_schema?.fields;
    if (!fields?.length) { if (showUI) toast.error(`No specs fields defined for ${typeCode}`); else console.warn(`[AutoFill] Skipped: no specs schema for type ${typeCode}`); return; }

    const fieldList = fields.map((f: any) => {
      let desc = f.key + ' (' + f.label + ', type: ' + f.type;
      if (f.options) desc += ', options: [' + f.options.join(', ') + ']';
      if (f.unit) desc += ', unit: ' + f.unit;
      desc += ')';
      return desc;
    }).join('\n  ');

    if (showUI) setIsAutoFilling(true);
    try {
      const data = await apiPost<any>('/api/groq', {
          model: GROQ_MODEL,
          messages: [
            {
              role: 'system',
              content: 'You are a PC hardware expert. Given a component\'s part number and description, extract its specifications. Return ONLY a JSON object with the specified field keys. Use null for any value you cannot determine with confidence. For boolean fields use true/false. For number fields use numeric values without units. For select fields use EXACTLY one of the provided options or null. For multi_select fields return an array of matching options.',
            },
            {
              role: 'user',
              content: `Part Number: ${partNumber}\nDescription: ${description}\n\nExtract these fields:\n  ${fieldList}`,
            },
          ],
          temperature: 0.1,
          // Reasoning tokens count towards the completion, so this needs
          // headroom well beyond the size of the JSON itself.
          max_tokens: 4000,
          response_format: jsonSchemaFormat('component_specs', specsSchema(fields)),
      });

      const specs = JSON.parse(data.choices[0].message.content);

      // Filter to only known field keys, skip nulls
      const cleanSpecs: Record<string, any> = {};
      for (const f of fields) {
        if (specs[f.key] != null) cleanSpecs[f.key] = specs[f.key];
      }
      if (Object.keys(cleanSpecs).length === 0) { if (showUI) toast.error('AI could not determine any specs'); else console.warn('[AutoFill] Skipped: Groq returned no usable specs'); return; }

      // Merge with existing specs — AI fills only empty/null fields, never overwrites
      const { data: existing } = await supabase.from('components').select('specs').eq('id', componentId).single();
      const merged = { ...cleanSpecs, ...(existing?.specs as Record<string, any> || {}) };
      await supabase.from('components').update({ specs: merged }).eq('id', componentId);
      queryClient.invalidateQueries({ queryKey: ['component', componentId] });
      toast.success(`Specs auto-filled (${Object.keys(cleanSpecs).length} fields)`);
    } catch (err: any) {
      if (showUI) toast.error(err.message || 'Auto-fill failed');
      else console.warn('[AutoFill] Failed:', err);
    } finally {
      if (showUI) setIsAutoFilling(false);
    }
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (values: any) => {
      // Derive available_qty from total_qty (reserved_qty is 0 for new components).
      // A pasted part number can carry stray spaces, which nothing else matches.
      const insertValues = {
        ...values,
        part_number: String(values.part_number ?? '').trim(),
        available_qty: Math.max(0, values.total_qty - (values.reserved_qty ?? 0)),
      };
      const { data, error } = await supabase
        .from('components')
        .insert([insertValues])
        .select('id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      toast.success('Component created');
      await writeAuditLog({
        action: 'component_created',
        entity_type: 'component',
        entity_id: data.id,
        entity_label: newForm.part_number,
        new_value: newForm,
      });
      // replace, not push: /components/new and /components/:id are the same
      // route, so backing into the create form does not remount this component
      // and it returns still holding the component just saved - ready to save a
      // duplicate. Replacing sends Back to the components list instead.
      navigate(`/components/${data.id}`, { replace: true });
      // Fire-and-forget: auto-fill specs via Groq
      autoFillSpecs(data.id, newForm.part_number, newForm.description, newForm.category);
    },
    onError: (error: any) => toast.error(error.message || 'Failed to create component'),
  });

  // Quantity adjustment mutation
  const adjustQtyMutation = useMutation({
    mutationFn: async (values: { total_qty: number; on_order_qty: number; notes: string }) => {
      const reservedQty = component?.reserved_qty ?? 0;
      const newAvailable = Math.max(0, values.total_qty - reservedQty);
      const { error } = await supabase
        .from('components')
        .update({ total_qty: values.total_qty, available_qty: newAvailable, on_order_qty: values.on_order_qty })
        .eq('id', componentId!);
      if (error) throw error;
      return values;
    },
    onSuccess: async (values) => {
      if (component?.part_number) {
        await fifoReallocate([component.part_number]);
      }
      queryClient.invalidateQueries({ queryKey: ['component', id] });
      queryClient.invalidateQueries({ queryKey: ['components'] });
      setIsAdjusting(false);
      toast.success('Stock quantities updated');
      await writeAuditLog({
        action: 'qty_adjustment',
        entity_type: 'component',
        entity_id: componentId,
        entity_label: component?.part_number,
        old_value: {
          total_qty: component?.total_qty,
          on_order_qty: component?.on_order_qty,
        },
        new_value: {
          total_qty: values.total_qty,
          on_order_qty: values.on_order_qty,
        },
        notes: values.notes || undefined,
      });
      refetchAudit();
    },
    onError: (error: any) => toast.error(error.message || 'Failed to adjust quantities'),
  });

  // Specs update mutation
  const updateSpecsMutation = useMutation({
    mutationFn: async (specs: Record<string, any>) => {
      const { error } = await supabase
        .from('components')
        .update({ specs })
        .eq('id', componentId!);
      if (error) throw error;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['component', id] });
      setIsEditingSpecs(false);
      toast.success('Specifications updated');
      await writeAuditLog({
        action: 'specs_updated',
        entity_type: 'component',
        entity_id: componentId,
        entity_label: component?.part_number,
        old_value: component?.specs || {},
        new_value: specsForm,
      });
      refetchAudit();
    },
    onError: (error: any) => toast.error(error.message || 'Failed to update specs'),
  });

  const handleEditSpecs = () => {
    setSpecsForm({ ...(component?.specs || {}) });
    setIsEditingSpecs(true);
  };

  const handleEdit = () => {
    setEditForm({
      description: component?.description || '',
      category: component?.category || '',
      supplier: component?.supplier || '',
      current_price: component?.current_price ?? 0,
      status: component?.status || 'Active',
    });
    setIsEditing(true);
  };

  const handleStartAdjust = () => {
    setAdjustForm({
      total_qty: component?.total_qty ?? 0,
      on_order_qty: component?.on_order_qty ?? 0,
      notes: '',
    });
    setIsAdjusting(true);
  };

  // Action badge colour helper
  const getActionBadgeClass = (action: string) => {
    if (action.includes('deleted')) return 'bg-red-100 text-red-700';
    if (action.includes('created')) return 'bg-green-100 text-green-700';
    if (action === 'qty_adjustment') return 'bg-blue-100 text-blue-700';
    return 'bg-gray-100 text-gray-700';
  };

  const formatAction = (action: string) =>
    action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // ── New Component Form ─────────────────────────────────────────────────────
  if (isNew) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold">Add Component</h1>
        </div>

        <Card>
          <CardHeader><CardTitle>Component Details</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Part Number <span className="text-red-500">*</span></label>
                <Input
                  value={newForm.part_number}
                  onChange={e => setNewForm({ ...newForm, part_number: e.target.value })}
                  className={cn('mt-1 font-mono', existingId && 'border-red-400 bg-red-50 focus-visible:ring-red-400')}
                  placeholder="e.g. CPU-AMD-R7-9800X3D"
                />
                {existingId && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    A component with this part number already exists —
                    <Link to={`/components/${existingId}`} className="underline">open it</Link>
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={newForm.description}
                  onChange={e => setNewForm({ ...newForm, description: e.target.value })}
                  className="mt-1"
                  placeholder="e.g. AMD Ryzen 7 9800X3D"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Category</label>
                <Input
                  list="new-category-options"
                  value={newForm.category}
                  onChange={e => setNewForm({ ...newForm, category: e.target.value })}
                  className="mt-1"
                  placeholder="e.g. CPU"
                />
                <datalist id="new-category-options">
                  {(categories ?? []).map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div>
                <label className="text-sm font-medium">Supplier</label>
                <Input
                  list="new-supplier-options"
                  value={newForm.supplier}
                  onChange={e => setNewForm({ ...newForm, supplier: e.target.value })}
                  className="mt-1"
                  placeholder="Select or type supplier"
                />
                <datalist id="new-supplier-options">
                  {(suppliers ?? []).map(s => <option key={s} value={s} />)}
                </datalist>
              </div>
              {canViewCosts && (
                <div>
                  <label className="text-sm font-medium">Current Price (€)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newForm.current_price}
                    onChange={e => setNewForm({ ...newForm, current_price: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
              )}
              <div>
                <label className="text-sm font-medium">Status</label>
                <select
                  value={newForm.status}
                  onChange={e => setNewForm({ ...newForm, status: e.target.value })}
                  className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="Active">Active</option>
                  <option value="Discontinued">Discontinued</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Total Stock (in warehouse)</label>
                <Input
                  type="number"
                  value={newForm.total_qty}
                  onChange={e => setNewForm({ ...newForm, total_qty: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">On Order Qty</label>
                <Input
                  type="number"
                  value={newForm.on_order_qty}
                  onChange={e => setNewForm({ ...newForm, on_order_qty: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate(newForm)}
            disabled={!newForm.part_number.trim() || !!existingId || createMutation.isPending}
          >
            <Save className="h-4 w-4 mr-2" />
            {createMutation.isPending ? 'Creating…' : 'Create Component'}
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!component) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Component not found</p>
        <Button className="mt-4" onClick={() => navigate(-1)}>Back to Components</Button>
      </div>
    );
  }

  const avail = component.available_qty ?? 0;
  const isOutOfStock = avail <= 0;
  const isOverReserved = avail < 0;
  const totalQty = component.total_qty ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              {listPosition && (
                <>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={goPrev} disabled={!hasPrev} title="Previous component">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-muted-foreground px-1 min-w-[3rem] text-center">{listPosition}</span>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={goNext} disabled={!hasNext} title="Next component">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
            {/* Component image thumbnail */}
            {componentImages.length > 0 && (
              <div className="shrink-0 cursor-pointer sm:hidden" onClick={() => setLightboxUrl(componentImages[0].url)}>
                <img
                  src={componentImages[0].url}
                  alt={component.part_number}
                  className="w-32 h-32 object-cover rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                />
              </div>
            )}
          </div>
          {/* Component image thumbnail — desktop */}
          {componentImages.length > 0 && (
            <div className="hidden sm:block shrink-0 cursor-pointer" onClick={() => setLightboxUrl(componentImages[0].url)}>
              <img
                src={componentImages[0].url}
                alt={component.part_number}
                className="w-48 h-48 object-cover rounded-lg border shadow-sm hover:shadow-md transition-shadow"
              />
            </div>
          )}
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              {isEditingSku ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={skuForm}
                    onChange={e => setSkuForm(e.target.value)}
                    className="text-2xl font-bold font-mono h-10 w-48"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter' && skuForm.trim() && skuForm.trim() !== component.part_number) skuRenameMutation.mutate(skuForm);
                      if (e.key === 'Escape') setIsEditingSku(false);
                    }}
                  />
                  <Button size="sm" onClick={() => skuRenameMutation.mutate(skuForm)} disabled={skuRenameMutation.isPending || !skuForm.trim() || skuForm.trim() === component.part_number}>
                    <Save className="h-3 w-3 mr-1" />{skuRenameMutation.isPending ? 'Saving…' : 'Save'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setIsEditingSku(false)}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <h1 className="text-2xl font-bold font-mono cursor-default group flex items-center gap-1" onClick={() => { if (isSuperAdmin) { setSkuForm(component.part_number); setIsEditingSku(true); } }}>
                  {component.part_number}
                  {isSuperAdmin && <Edit className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />}
                  {isSuperAdmin && <HelpTooltip tipKey="componentDetail.skuRename" />}
                </h1>
              )}
              <Badge className={cn('text-xs', isOverReserved ? 'bg-orange-100 text-orange-700' : isOutOfStock ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700')}>
                {isOverReserved ? 'Over-Reserved' : isOutOfStock ? 'Out of Stock' : 'In Stock'}
              </Badge>
              <Badge variant="outline">{component.status}</Badge>
            </div>
            <p className="text-muted-foreground mt-1">{component.description || 'No description'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                <X className="h-4 w-4 mr-2" />Cancel
              </Button>
              <Button onClick={() => updateMutation.mutate(editForm)} disabled={updateMutation.isPending}>
                <Save className="h-4 w-4 mr-2" />{updateMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              {canManage && (
                <Button variant="outline" onClick={handleEdit}>
                  <Edit className="h-4 w-4 mr-2" />Edit
                </Button>
              )}
              {isSuperAdmin && (
                <Button variant="destructive" disabled={deleteLoading} onClick={async () => {
                  if (!component?.part_number) return;
                  setDeleteLoading(true);
                  const pn = component.part_number;
                  const [b, r, o] = await Promise.all([
                    supabase.from('build_components').select('id', { count: 'exact', head: true }).eq('component_sku', pn),
                    supabase.from('reservation_components').select('id', { count: 'exact', head: true }).eq('component_sku', pn),
                    supabase.from('assembly_order_components').select('id', { count: 'exact', head: true }).eq('component_sku', pn),
                  ]);
                  setDeleteUsage({ builds: b.count || 0, reservations: r.count || 0, orders: o.count || 0 });
                  setDeleteLoading(false);
                  setShowDeleteConfirm(true);
                }}>
                  <Trash2 className="h-4 w-4 mr-2" />{deleteLoading ? 'Checking…' : 'Delete'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirm */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader><CardTitle className="text-red-600">Delete Component</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p>Are you sure you want to delete <strong>{component.part_number}</strong>?</p>
              {deleteUsage && (deleteUsage.builds > 0 || deleteUsage.reservations > 0 || deleteUsage.orders > 0) ? (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm space-y-1">
                    <p className="font-medium text-amber-800">This component is currently in use:</p>
                    <ul className="list-disc list-inside text-amber-700">
                      {deleteUsage.builds > 0 && <li>{deleteUsage.builds} build BOM{deleteUsage.builds > 1 ? 's' : ''}</li>}
                      {deleteUsage.reservations > 0 && <li>{deleteUsage.reservations} reservation{deleteUsage.reservations > 1 ? 's' : ''}</li>}
                      {deleteUsage.orders > 0 && <li>{deleteUsage.orders} assembly order{deleteUsage.orders > 1 ? 's' : ''}</li>}
                    </ul>
                    <p className="text-amber-600 mt-2">Deleting will remove this component from the system. References in builds, reservations, and orders will become orphaned.</p>
                  </div>
                  <p className="text-sm text-muted-foreground">Consider setting the status to <strong>Inactive</strong> instead.</p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">This component is not used in any builds, reservations, or orders.</p>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                <Button variant="destructive" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete Anyway'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">

          {/* Stock Info */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle>Stock Information <HelpTooltip tipKey="componentDetail.stockAdjust" /></CardTitle>
              {canManage && !isAdjusting && (
                <Button variant="outline" size="sm" onClick={handleStartAdjust}>
                  <SlidersHorizontal className="h-4 w-4 mr-2" />Adjust
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Available</p>
                  <p className={cn('text-2xl font-bold', isOverReserved ? 'text-orange-600' : isOutOfStock ? 'text-red-600' : 'text-green-600')}>{avail}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Reserved</p>
                  {(component.reserved_qty ?? 0) > 0 ? (
                    <Link
                      to={`/reservations?component=${encodeURIComponent(component.part_number)}`}
                      className="text-2xl font-bold text-blue-600 hover:underline"
                    >
                      {component.reserved_qty}
                    </Link>
                  ) : (
                    <p className="text-2xl font-bold text-blue-600">0</p>
                  )}
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Expected</p>
                  <p className="text-2xl font-bold text-amber-600">{component.expected_qty ?? 0}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">On Order</p>
                  <p className="text-2xl font-bold text-purple-600">{component.on_order_qty ?? 0}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold">{totalQty}</p>
                </div>
              </div>

              {/* Inline adjust form */}
              {isAdjusting && (
                <div className="border rounded-lg p-4 space-y-3 bg-blue-50/50">
                  <p className="text-sm font-medium text-muted-foreground">Adjust stock quantities</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium">Total Stock (in warehouse)</label>
                      <Input
                        type="number"
                        value={adjustForm.total_qty}
                        onChange={e => setAdjustForm(f => ({ ...f, total_qty: parseInt(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">On Order Qty</label>
                      <Input
                        type="number"
                        value={adjustForm.on_order_qty}
                        onChange={e => setAdjustForm(f => ({ ...f, on_order_qty: parseInt(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Reason / Notes</label>
                    <Input
                      value={adjustForm.notes}
                      onChange={e => setAdjustForm(f => ({ ...f, notes: e.target.value }))}
                      className="mt-1"
                      placeholder="e.g. Stock count correction, received delivery..."
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => setIsAdjusting(false)}>
                      <X className="h-4 w-4 mr-1" />Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => adjustQtyMutation.mutate(adjustForm)}
                      disabled={adjustQtyMutation.isPending}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {adjustQtyMutation.isPending ? 'Saving…' : 'Save Quantities'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Details */}
          <Card>
            <CardHeader><CardTitle>Details</CardTitle></CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="text-sm font-medium">Description</label>
                    <Input value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Category</label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        list="category-options"
                        value={editForm.category}
                        onChange={e => setEditForm({ ...editForm, category: e.target.value })}
                        placeholder="e.g. CPU"
                      />
                      <datalist id="category-options">
                        {(categories ?? []).map(c => <option key={c} value={c} />)}
                      </datalist>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Supplier</label>
                    <Input
                      list="supplier-options"
                      value={editForm.supplier}
                      onChange={e => setEditForm({ ...editForm, supplier: e.target.value })}
                      className="mt-1"
                      placeholder="Select or type supplier"
                    />
                    <datalist id="supplier-options">
                      {(suppliers ?? []).map(s => <option key={s} value={s} />)}
                    </datalist>
                  </div>
                  {canViewCosts && (
                    <div>
                      <label className="text-sm font-medium">Current Price (€) <HelpTooltip tipKey="componentDetail.currentPrice" /></label>
                      <Input type="number" step="0.01" value={editForm.current_price} onChange={e => setEditForm({ ...editForm, current_price: parseFloat(e.target.value) || 0 })} className="mt-1" />
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium">Status</label>
                    <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm">
                      <option value="Active">Active</option>
                      <option value="Discontinued">Discontinued</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-medium">{component.category || '—'}</p>
                  </div>
                  {canViewCosts && (
                    <div>
                      <p className="text-sm text-muted-foreground">Supplier</p>
                      <p className="font-medium">{component.supplier || '—'}</p>
                    </div>
                  )}
                  {canViewCosts && (
                    <div>
                      <p className="text-sm text-muted-foreground">Current Price</p>
                      <p className="font-medium font-mono">{component.current_price != null ? formatCurrency(component.current_price) : '—'}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Last Updated</p>
                    <p className="font-medium">{component.updated_at ? formatDate(component.updated_at) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge variant="outline">{component.status}</Badge>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Specifications */}
          {specsFields.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Settings2 className="h-5 w-5" />
                  Specifications <HelpTooltip tipKey="componentDetail.specs" />
                  <span className="text-sm font-normal text-muted-foreground">({matchedType?.name})</span>
                </CardTitle>
                {canManage && !isEditingSpecs && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isAutoFilling}
                      onClick={() => autoFillSpecs(componentId!, component!.part_number, component!.description, component!.category, true)}
                    >
                      <Sparkles className={`h-4 w-4 mr-2 ${isAutoFilling ? 'animate-spin' : ''}`} />
                      {isAutoFilling ? 'Filling…' : 'AI Auto-fill'}
                    </Button>
                    <HelpTooltip tipKey="componentDetail.autoFill" />
                    <Button variant="outline" size="sm" onClick={handleEditSpecs}>
                      <Edit className="h-4 w-4 mr-2" />Edit Specs
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {isEditingSpecs ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {specsFields.map((field: any) => (
                        <div key={field.key}>
                          <label className="text-sm font-medium">{field.label}</label>
                          {field.type === 'boolean' ? (
                            <div className="mt-1 flex items-center h-10">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={specsForm[field.key] === true}
                                  onChange={e => setSpecsForm(f => ({ ...f, [field.key]: e.target.checked }))}
                                  className="h-4 w-4 rounded accent-primary cursor-pointer"
                                />
                                <span className="text-sm">{specsForm[field.key] ? 'Yes' : 'No'}</span>
                              </label>
                            </div>
                          ) : field.type === 'select' ? (
                            <select
                              value={specsForm[field.key] ?? ''}
                              onChange={e => setSpecsForm(f => ({ ...f, [field.key]: e.target.value || null }))}
                              className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                            >
                              <option value="">—</option>
                              {(field.options || []).map((opt: string) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : field.type === 'multi_select' ? (
                            <div className="mt-1 space-y-1">
                              {(field.options || []).map((opt: string) => {
                                const arr: string[] = specsForm[field.key] || [];
                                const checked = arr.includes(opt);
                                return (
                                  <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={e => {
                                        const next = e.target.checked
                                          ? [...arr, opt]
                                          : arr.filter((v: string) => v !== opt);
                                        setSpecsForm(f => ({ ...f, [field.key]: next }));
                                      }}
                                      className="h-3.5 w-3.5 rounded accent-primary cursor-pointer"
                                    />
                                    {opt}
                                  </label>
                                );
                              })}
                            </div>
                          ) : field.type === 'number' ? (
                            <Input
                              type="number"
                              step="any"
                              value={specsForm[field.key] ?? ''}
                              onChange={e => setSpecsForm(f => ({ ...f, [field.key]: e.target.value === '' ? null : Number(e.target.value) }))}
                              className="mt-1"
                              placeholder={field.unit || ''}
                            />
                          ) : (
                            <Input
                              value={specsForm[field.key] ?? ''}
                              onChange={e => setSpecsForm(f => ({ ...f, [field.key]: e.target.value || null }))}
                              className="mt-1"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setIsEditingSpecs(false)}>
                        <X className="h-4 w-4 mr-1" />Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateSpecsMutation.mutate(specsForm)}
                        disabled={updateSpecsMutation.isPending}
                      >
                        <Save className="h-4 w-4 mr-1" />
                        {updateSpecsMutation.isPending ? 'Saving…' : 'Save Specs'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-4">
                    {specsFields.map((field: any) => {
                      const val = (component?.specs as any)?.[field.key];
                      let display: string;
                      if (val == null || val === '') display = '—';
                      else if (field.type === 'boolean') display = val ? 'Yes' : 'No';
                      else if (Array.isArray(val)) display = val.length ? val.join(', ') : '—';
                      else display = String(val) + (field.unit ? ` ${field.unit}` : '');
                      return (
                        <div key={field.key}>
                          <p className="text-sm text-muted-foreground">{field.label}</p>
                          <p className="font-medium">{display}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Used In Builds */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Used In Builds ({usedInBuilds?.length ?? 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {usedInBuilds && usedInBuilds.length > 0 ? (
                <div className="space-y-2">
                  {usedInBuilds.map((build: any) => (
                    <div key={build.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <Link to={`/builds/${build.id}`} className="font-mono text-sm text-primary hover:underline shrink-0">
                          {build.sku}
                        </Link>
                        <span className="text-sm text-muted-foreground truncate">{build.description}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {build.isEol && (
                          <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">EOL</Badge>
                        )}
                        <Badge className={cn('text-xs',
                          build.status?.toLowerCase() === 'active' ? 'bg-green-100 text-green-800' :
                          build.status?.toLowerCase() === 'eol'    ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        )}>
                          {build.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">Not used in any builds</p>
              )}
            </CardContent>
          </Card>
          {/* Component Images (shown for case components, or any component with images) */}
          {(isCase || componentImages.length > 0) && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Images
                    {componentImages.length > 0 && (
                      <span className="text-sm font-normal text-muted-foreground">({componentImages.length})</span>
                    )}
                  </CardTitle>
                  {canManage && (
                    <>
                      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
                      <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={imgUploading}>
                        <Upload className="h-4 w-4 mr-1" />{imgUploading ? 'Uploading...' : 'Upload'}
                      </Button>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {componentImages.length === 0 ? (
                  <div className="text-center py-6">
                    <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                    <p className="text-sm text-muted-foreground">No images uploaded</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {componentImages.map((img: any) => (
                      <div key={img.id} className="group relative border rounded-lg overflow-hidden bg-muted/30">
                        <img src={img.url} alt={img.file_name || 'Component image'} className="w-full h-32 object-cover cursor-pointer" loading="lazy" onClick={() => setLightboxUrl(img.url)} />
                        <div className="p-1.5">
                          <p className="text-[10px] text-muted-foreground truncate">{img.file_name}</p>
                        </div>
                        {canManage && (
                          <button
                            className="absolute top-1.5 right-1.5 h-6 w-6 rounded bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => setDeleteImgId(img.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Delete image confirm */}
          {deleteImgId && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <Card className="w-full max-w-sm mx-4">
                <CardHeader><CardTitle>Delete image?</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">This will permanently remove the image.</p>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => setDeleteImgId(null)}>Cancel</Button>
                    <Button variant="destructive" size="sm" onClick={() => deleteImageMutation.mutate(deleteImgId)} disabled={deleteImageMutation.isPending}>
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card>
            <CardHeader><CardTitle>Quick Stats</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {canViewCosts && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Stock Value</span>
                  <span className="font-mono font-medium">
                    {component.current_price != null ? formatCurrency(component.current_price * totalQty) : '—'}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Builds Using</span>
                <span className="font-medium">{usedInBuilds?.length ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Active Builds</span>
                <span className="font-medium">
                  {usedInBuilds?.filter((b: any) => b.status?.toLowerCase() === 'active').length ?? 0}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Audit Log */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              {auditEntries && auditEntries.length > 0 ? (
                <div className="space-y-3">
                  {auditEntries.map((entry: any) => (
                    <div key={entry.id} className="space-y-1 text-sm border-b pb-2 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', getActionBadgeClass(entry.action))}>
                          {formatAction(entry.action)}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatDateTime(entry.created_at)}
                        </span>
                      </div>
                      {entry.user_email && (
                        <p className="text-xs text-muted-foreground">{entry.user_email}</p>
                      )}
                      {entry.notes && (
                        <p className="text-xs italic text-muted-foreground">"{entry.notes}"</p>
                      )}
                      {entry.action === 'qty_adjustment' && entry.old_value && entry.new_value && (
                        <div className="text-xs text-muted-foreground font-mono">
                          {entry.old_value.total_qty !== entry.new_value.total_qty && (
                            <span className="mr-3">
                              Total: {entry.old_value.total_qty} → <strong>{entry.new_value.total_qty}</strong>
                            </span>
                          )}
                          {/* Legacy entries may still use available_qty */}
                          {entry.old_value.available_qty !== undefined && entry.old_value.total_qty === undefined && entry.old_value.available_qty !== entry.new_value.available_qty && (
                            <span className="mr-3">
                              Avail: {entry.old_value.available_qty} → <strong>{entry.new_value.available_qty}</strong>
                            </span>
                          )}
                          {entry.old_value.on_order_qty !== entry.new_value.on_order_qty && (
                            <span>
                              On Order: {entry.old_value.on_order_qty} → <strong>{entry.new_value.on_order_qty}</strong>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4 text-sm">No activity recorded</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Image lightbox ── */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center cursor-pointer"
          onClick={() => setLightboxUrl(null)}
        >
          <img
            src={lightboxUrl}
            alt="Full size"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl font-light leading-none"
            onClick={() => setLightboxUrl(null)}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}
